import { SubjectAnswers } from '@surveycake/logic-engine';
import {
  Equality,
  useEngineInstance,
  useSubjectAnswers,
  UseSubjectAnswersSelector,
  useSubjectId,
  useSubjectOptionStates
} from '@surveycake/react-logic-engine';
import { debounce, passThrough } from '@surveycake/utils';
import { addOptionPrefix, useHandleAnswerFinish } from 'app/modules/subject';
import { useCallback, useMemo } from 'react';

/**
 * The factory to create the hook for binding answer to subject which is choice type.
 *
 * @param choiceType - `single` or `multiple`.
 * @param getValue - To get the value from answers of engine.
 * @param resolveChecked - To determine the option is checked or not.
 * @param resolvePayload - To determine the payload which will be set to engine.
 */
export function createUseChoiceAnswer<M extends 'single' | 'multiple'>(
  choiceType: M,
  getValue: (
    answers: SubjectAnswers
  ) => M extends 'multiple' ? string[] : string,
  resolveChecked: (checked: boolean, value: string) => boolean,
  resolvePayload: (
    value: string,
    checked: boolean,
    payload: string[]
  ) => string[]
) {
  interface AnswersResult {
    value: ReturnType<typeof getValue>;
    elseValue: string;
  }

  const answersSelectors: UseSubjectAnswersSelector<
    AnswersResult
  > = answers => {
    const { meta } = answers;
    return {
      value: getValue(answers),
      elseValue: (meta && meta.else && meta.else.value) || ''
    };
  };

  const answersEquality: Equality<AnswersResult> = (prev, next) =>
    prev.value === next.value && prev.elseValue === next.elseValue;

  return function useChoiceAnswer() {
    const engine = useEngineInstance();
    const id = useSubjectId();
    const { options, extras } = useMemo(() => engine.getSubjectData(id), [
      engine,
      id
    ]);
    const optionStates = useSubjectOptionStates(id, passThrough);
    const answersResult = useSubjectAnswers(
      id,
      answersSelectors,
      answersEquality
    );
    const elseOption = useMemo(() => {
      const lastOption = options ? options[options.length - 1] : undefined;
      const lastSecondOption = options
        ? options[options.length - 2]
        : undefined;

      if (lastOption && lastOption.elses === 1) {
        return lastOption;
      } else if (lastSecondOption && lastSecondOption.elses === 1) {
        return lastSecondOption;
      }

      return undefined;
    }, [options]);
    const onElseChange = useCallback(
      async (value: string) => {
        if (!elseOption) {
          return;
        }

        const { payload } = engine.getSubjectAnswers(id);

        await engine.setAnswer(id, {
          payload,
          meta: {
            else: {
              id: elseOption.ID,
              value
            }
          }
        });
      },
      [engine, id, elseOption]
    );
    /**
     * Since choiceType is from factory param, the condition here won't cause the runtime error.
     */
    const useHandleAnswerFinishValue = useHandleAnswerFinish(engine, id);
    const handleAnswerFinish =
      choiceType === 'single' ? useHandleAnswerFinishValue : undefined;
    const debouncedHandleAnswerFinishValue = useMemo(
      () => debounce(500)(handleAnswerFinish!),
      [handleAnswerFinish]
    );
    const debouncedHandleAnswerFinish = handleAnswerFinish
      ? debouncedHandleAnswerFinishValue
      : undefined;
    const onElseFinishedValue = useCallback(
      async (value: string) => {
        await onElseChange(value);

        if (debouncedHandleAnswerFinish) {
          debouncedHandleAnswerFinish();
        }
      },
      [onElseChange, debouncedHandleAnswerFinish]
    );
    const onElseFinished =
      choiceType === 'single' ? onElseFinishedValue : undefined;
    const onChange = useCallback(
      async (value: string, checked: boolean) => {
        const { payload, meta } = engine.getSubjectAnswers(id);
        const resolvedChecked = resolveChecked(checked, value);

        await engine.setAnswer(id, {
          payload: resolvePayload(value, resolvedChecked, payload as string[]),
          meta
        });

        if (
          debouncedHandleAnswerFinish &&
          !(elseOption && addOptionPrefix(elseOption.ID) === value)
        ) {
          debouncedHandleAnswerFinish(!resolvedChecked);
        }
      },
      [engine, id, elseOption, debouncedHandleAnswerFinish]
    );

    return {
      columnDesktop: extras && extras.column ? extras.column : 2,
      columnMobile: extras && extras.column_m ? extras.column_m : 1,
      options,
      optionStates,
      value: answersResult.value,
      elseValue: answersResult.elseValue,
      elseOption,
      onChange,
      onElseChange,
      onElseFinished
    };
  };
}
